<script setup>
import { usePage } from '@inertiajs/vue3'
import { Bars3Icon } from '@heroicons/vue/24/outline'
import { computed, nextTick, onMounted, ref } from 'vue'
import { Dropdown, DropdownItem } from '@codinglabsau/ui'
import { Sheet, SheetContent } from '@codinglabsau/gooey'
import { ExclamationTriangleIcon, TagIcon, AcademicCapIcon } from '@heroicons/vue/24/solid'
import { ChevronDownIcon, ChevronUpIcon, ArrowLeftIcon, ArrowRightIcon } from 'lucide-vue-next'
import { setAutocompleteContainer, autocompleteSearch } from '../../Search/autocomplete.js'
import TenantsGroupLinks from './TenantsGroupLinks.vue'
import HeaderCounterGroup from './HeaderCounterGroup.vue'
import UserDropdown from './UserDropdown.vue'
import { DollarSignIcon } from '@/Components'
import HeaderCounter from './HeaderCounter.vue'
import { syncState } from '@/Stores/messagesDropdown.js'
import { useHeaderBanners } from '@/Composables/useHeaderBanners'
import { ShoppingCartIcon } from '@heroicons/vue/24/solid/index.js'
import BlockRenderer from '../Inputs/BlockEditor/BlockRenderer.vue'

const page = usePage()

const { liveEventBanner } = useHeaderBanners()

const outage = page.props.header.outage

const siteNotification = page.props.header.site_notification

const currentLiveEventBanner = computed(() => liveEventBanner.value)

const indexes = page.props.header.indexes

const topCategories = page.props.header.top_categories

const favouriteStores = page.props.header.favourite_stores

const topContents = page.props.header.top_contents

const tenantsInGroupNoReserveCategories = usePage().props.header.tenants_in_groups_no_reserve_categories

const autocompleteContainer = ref(null)

const showMobileMenu = ref(false)

const showStoresMenu = ref(false)

const showNoReserveMenu = ref(false)

const showLearnMenu = ref(false)

const isPwa = ref(false)
const isIOS = ref(false)

const cartCounter = computed(() => {
  return {
    href: route('cart.show'),
    counter: usePage().props.header.counters.wins,
    icon: ShoppingCartIcon,
    title: __('Cart'),
    enabled: true,
  }
})

onMounted(() => {
  if (page.props.auth) {
    syncState(page)
  }

  setAutocompleteContainer(autocompleteContainer.value)

  nextTick(() => {
    autocompleteSearch()
  })

  isPwa.value = window.matchMedia('(display-mode: standalone)').matches
  isIOS.value = /iPad|iPhone|iPod/.test(navigator.userAgent)
})
</script>

<template>
  <header class="shrink-0 bg-white border-b border-gray-200">

    <!-- Outage Notification -->
    <div
      v-if="outage"
      class="bg-yellow-50 text-yellow-800 flex text-sm justify-center"
    >
      <div class="flex items-start max-w-7xl px-6 py-3 lg:items-center w-full">
        <ExclamationTriangleIcon class="w-5 h-5 mr-2 mt-1 text-yellow-400 flex-shrink-0" />
        <span
          class="whitespace-normal"
        >
           {{
            __(':site will be undergoing scheduled maintenance from :starts to :ends. We apologise for any inconvenience caused.', {
              site: $page.props.tenant.name,
              starts: outage.outage_starts_at ? $moment.utc(outage.outage_starts_at).format('Do MMMM YYYY h:mm a z') : $moment.utc(outage.notification_starts_at).format('Do MMMM YYYY h:mm a z'),
              ends: outage.outage_ends_at ? $moment.utc(outage.outage_ends_at).format('Do MMMM YYYY h:mm a z') : $moment.utc(outage.notification_ends_at).format('Do MMMM YYYY h:mm a z'),
            })
          }}
        </span>
      </div>
    </div>

    <!-- Site Notification -->
    <div
      v-if="siteNotification && ! currentLiveEventBanner"
      class="text-center bg-sky-800 py-1 text-white text-sm"
      v-html="siteNotification"
    >
    </div>

    <div
      v-if="currentLiveEventBanner"
      class="text-center bg-red-600 py-2 animate-temporary-pulse text-white text-sm">
      <a :href="currentLiveEventBanner.href">
        {{ currentLiveEventBanner.heading }} -
        <b>{{__('Click to Watch!')}}</b>
      </a>
    </div>

    <!-- Tenant Groups -->
    <div class="bg-zinc-300 w-full text-black text-xs h-6 flex">
      <TenantsGroupLinks />
    </div>

    <div class="flex flex-wrap lg:flex-nowrap items-center mb-2 justify-between w-full px-2 md:px-4 my-2 gap-3">
      <!-- Mobile menu button -->
      <div class="flex">
        <button
          class="mt-3 mr-2 text-sm text-gray-700 lg:hidden"
          @click="showMobileMenu = !showMobileMenu"
        >
          <Bars3Icon class="w-6 h-6 text-gray-500" />
        </button>

        <!-- Logo -->
        <a :href="route('index')">
          <img
            class="w-56 hidden lg:flex"
            :src="tenantAsset('theme/images/logo.png')"
            :alt="$page.props.tenant.site_short_name"
          >
          <img
            class="size-8 mt-2 flex lg:hidden"
            :src="tenantAsset('theme/images/icons/64x64.png')"
            :alt="$page.props.tenant.site_short_name"
          >
        </a>
      </div>

      <!-- Search -->
      <div class="max-w-2xl flex lg:flex-1 md:max-w-md lg:order-2 lg:min-w-sm min-w-lg order-3 w-full">
        <div v-show="isIOS && isPwa" class="flex gap-2 mr-2 text-gray-500 hover:text-gray-600">
          <button onclick="history.back()">
            <ArrowLeftIcon class="size-5"/>
          </button>

          <button onclick="history.forward()">
            <ArrowRightIcon class="size-5"/>
          </button>
        </div>

        <form method="get" :action="route('search')" role="search" class="flex flex-grow">
          <div
            id="autocomplete"
            ref="autocompleteContainer"
            class="min-w-full w-full"
            :data-search-url="route('search')"
            :data-index-auction="indexes['auctions']"
            :data-index-auction-categories="indexes['auctionCategories']"
            :data-index-content="indexes['contents']"
            :data-index-help="indexes['help']"
            :data-index-store="indexes['stores']"
            :data-index-query-suggestions="indexes['querySuggestions']"
            :data-index-quick-picks="indexes['quickPicks']"
          />
        </form>
      </div>


      <div class="flex mt-3 lg:order-3 md:gap-2 order-2">
        <!-- Counters -->
        <HeaderCounterGroup
          :cart="cartCounter"
          v-if="$page.props.auth"
          class="mr-2 lg:mr-4 md:mr-0"
        />

        <!-- User Dropdown -->
        <UserDropdown v-if="$page.props.auth" />

        <div
          v-else
          class="flex items-center space-x-4 text-gray-600 lg:order-3 order-2"
        >
          <a :href="route('register')" class="text-sm hover:underline">
            {{ __('Register') }}
          </a>
          <a :href="route('login')" class="text-sm hover:underline">
            {{ __('Login') }}
          </a>
          <HeaderCounter
            :key="cartCounter.title"
            class="hover:underline"
            v-bind="cartCounter"
          />
        </div>
      </div>
    </div>

    <!-- Desktop Menu -->
    <div
      class="mt-5 lg:flex lg:flex-row flex-col lg:items-center justify-between gap-2 lg:px-6 pb-3 text-sm text-gray-700 hidden"
    >
      <BlockRenderer
        v-if="$page.props.header.features['dynamic-header']"
        v-bind="$page.props.header.widgets.sub_header_navigation"
      />
      <div v-else class="flex items-center mt-2">
        <div class="pr-4">
          <a
            :href="route('auctions.index')"
            class="hover:text-gray-900 hover:underline flex cursor-pointer relative"
          >
            {{ __('Categories') }}
          </a>
        </div>

        <div
          v-if="tenantsInGroupNoReserveCategories.length > 0"
          class="px-4"
        >
          <Dropdown
            text-classes="border-none !shadow-none focus:!ring-0 focus:!ring-offset-0 !px-2 mx-1 mt-1 hover:text-gray-900 hover:underline flex cursor-pointer relative items-center"
          >
            <template #text>
              <div class="flex items-center">
                <DollarSignIcon class="w-4 h-4 mr-1" />
                {{ __('No Reserve') }}
                <ChevronDownIcon class="w-4 h-4 ml-1" />
              </div>
            </template>

            <div v-for="category in tenantsInGroupNoReserveCategories">
              <DropdownItem
                :href="category['href']"
                external
              >
                <div class="flex items-center">
                  <img
                    :src="category['tenant_logo']"
                    :alt="category['tenant_name']"
                    class="w-5 mr-2"
                  >
                  {{ __('No Reserve :tenant', { 'tenant': category['tenant_name'] }) }}
                </div>
              </DropdownItem>
            </div>
          </Dropdown>
        </div>

        <div v-for="category in topCategories" class="px-4">
          <a
            :href="route('categories.show', category.slug)"
            class="hover:text-gray-900 hover:underline mx-1.5 flex cursor-pointer relative"
          >
            {{ category.name }}
          </a>
        </div>
      </div>

      <div class="flex mx-1 place-self-end">
        <!-- Stores -->
        <Dropdown
          text-classes="border-none !shadow-none focus:!ring-0 focus:!ring-offset-0 !px-2 mx-1 mt-1 hover:text-gray-900 hover:underline flex cursor-pointer relative items-center"
        >
          <template #text>
            <div class="flex items-center">
              <TagIcon class="w-4 h-4 mr-1" />
              {{ __('Stores') }}
              <ChevronDownIcon class="w-4 h-4 ml-1" />
            </div>
          </template>

          <DropdownItem
            :href="route('stores.index')"
            external
          >
            {{ __('All Stores') }}
          </DropdownItem>

          <div v-if="favouriteStores && favouriteStores.length > 0" class="border-t">
            <DropdownItem
              v-for="favouriteStore in favouriteStores"
              :key="favouriteStore.id"
              :href="route('stores.show', favouriteStore.url_nick)"
              external
            >
              {{ favouriteStore.nick }}
            </DropdownItem>
          </div>
        </Dropdown>

        <Dropdown
          text-classes="border-none !shadow-none focus:!ring-0 focus:!ring-offset-0 !px-2 mx-1 mt-1 hover:text-gray-900 hover:underline flex cursor-pointer relative items-center"
          scroll-height="max-h-[40vh]"
        >
          <template #text>
            <div class="flex items-center">
              <AcademicCapIcon class="w-4 h-4 mr-1" />
              {{ __('Learn') }}
              <ChevronDownIcon class="w-4 h-4 ml-1" />
            </div>
          </template>

          <DropdownItem
            :href="route('learn.index')"
            external
          >
            {{ __($page.props.tenant.settings.learn_name) }}
          </DropdownItem>

          <div v-if="topContents && topContents.length > 0" class="border-t">
            <DropdownItem
              v-for="content in topContents"
              :key="content.id"
              :href="content.url"
              external
            >
              {{ __(content.name) }}
            </DropdownItem>
          </div>
        </Dropdown>
      </div>
    </div>

    <!-- Mobile Menu -->
    <Sheet :open="showMobileMenu" @update:open="showMobileMenu = !showMobileMenu">
      <SheetContent class="overflow-y-auto" side="left">
        <div
          class="flex flex-col text-gray-700 space-y-2"
        >
          <div v-if="$page.props.header.features['dynamic-header']">
            <BlockRenderer
              v-bind="$page.props.header.widgets.sub_header_navigation"
            />
          </div>
          <template v-else>
            <div>
              <a
                :href="route('auctions.index')"
                class="hover:text-gray-900 hover:underline flex cursor-pointer"
              >
                {{ __('Categories') }}
              </a>
            </div>

            <!-- No Reserve -->
            <div v-if="tenantsInGroupNoReserveCategories.length > 0">
              <button
                @click="showNoReserveMenu = !showNoReserveMenu"
                class="flex items-center py-2 px-4 hover:underline cursor-pointer w-full"
                :class="{'bg-gray-100': showNoReserveMenu }"
              >
                <DollarSignIcon class="w-4 h-4 mr-1" />
                {{ __('No Reserve') }}
                <ChevronDownIcon class="w-4 h-4 ml-1" />
              </button>

              <div
                v-show="showNoReserveMenu"
                v-for="category in tenantsInGroupNoReserveCategories"
              >
                <div class="px-6 flex items-center w-full hover:underline py-2 cursor-pointer">
                  <a :href="category['href']" class="flex items-center w-full ">
                    <img
                      :src="category['tenant_logo']"
                      :alt="category['tenant_name']"
                      class="w-5 mr-2"
                    >
                    {{ __('No Reserve :tenant', { 'tenant': category['tenant_name'] }) }}
                  </a>
                </div>
              </div>
            </div>

            <!-- Categories -->
            <div v-for="category in topCategories" class="px-4">
              <a
                :href="route('categories.show', category.slug)"
                class="mt-1 hover:text-gray-900 hover:underline flex cursor-pointer relative"
              >
                {{ category.name }}
              </a>
            </div>
          </template>

          <!-- Stores -->
          <div>
            <button
              @click="showStoresMenu = !showStoresMenu"
              class="flex items-center justify-between py-2 px-4 hover:underline cursor-pointer w-full"
              :class="{'bg-gray-100': showStoresMenu }"
            >
              <div class="flex items-center">
                <TagIcon class="w-4 h-4 mr-1" />
                {{ __('Stores') }}
              </div>
              <ChevronDownIcon v-if="!showStoresMenu" class="w-4 h-4" />
              <ChevronUpIcon v-if="showStoresMenu" class="w-4 h-4" />
            </button>

            <div v-show="showStoresMenu">
              <a
                :href="route('stores.index')"
                class="px-6 flex items-center w-full hover:underline py-2 cursor-pointer"
              >
                {{ __('All Stores') }}
              </a>

              <div v-if="favouriteStores && favouriteStores.length > 0" class="border-t">
                <a
                  v-for="favouriteStore in favouriteStores"
                  :key="favouriteStore.id"
                  :href="route('stores.show', favouriteStore.url_nick)"
                  class="px-6 flex items-center w-full hover:underline py-2 cursor-pointer"
                >
                  {{ favouriteStore.nick }}
                </a>
              </div>
            </div>
          </div>

          <!-- Learn -->
          <div>
            <button
              @click="showLearnMenu = !showLearnMenu"
              class="flex items-center justify-between py-2 px-4 hover:underline cursor-pointer w-full"
              :class="{'bg-gray-100': showLearnMenu }"
            >
              <div class="flex items-center">
                <AcademicCapIcon class="w-4 h-4 mr-1" />
                {{ __('Learn') }}
              </div>

              <ChevronDownIcon v-if="!showLearnMenu" class="w-4 h-4" />
              <ChevronUpIcon v-if="showLearnMenu" class="w-4 h-4" />

            </button>

            <div v-show="showLearnMenu">
              <a
                :href="route('learn.index')"
                class="px-6 flex items-center w-full hover:underline py-2 cursor-pointer"
              >
                {{ __($page.props.tenant.settings.learn_name) }}
              </a>

              <div v-if="topContents && topContents.length > 0" class="border-t">
                <a
                  v-for="content in topContents"
                  :key="content.id"
                  :href="content.url"
                  class="px-6 flex items-center w-full hover:underline py-2 cursor-pointer"
                >
                  {{ __(content.name) }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  </header>
</template>
